import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardActionArea, CardContent, Chip,
  CircularProgress, Grid, InputAdornment, Link, MenuItem, Paper, TextField, Tooltip, Typography, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import { zkApi } from "./zkApi";
import { apiError, chipSx } from "./zkStatusStyles";
import { backButtonSx } from "../hpcAdminStatusStyles";
import DoorAccessDialog, { backToNormal } from "./DoorAccessDialog";
import ZkDoorAccess from "./ZkDoorAccess";
import {
  SIMPLE_FILTERS, actionFor, displayId, displayName, doorStatus, isVisible, matchesSearch, pendingNote, plainReason,
  statusLine, summarize, timeOf,
} from "./doorAccessText";

const ADVANCED_KEY = "teba.zk.doorAdvancedOpen";
const REFUSED_SHOWN = 20;

const readAdvanced = () => {
  try { return window.localStorage.getItem(ADVANCED_KEY) === "1"; } catch (e) { return false; }
};
const writeAdvanced = (open) => {
  try { window.localStorage.setItem(ADVANCED_KEY, open ? "1" : "0"); } catch (e) { /* private window etc. */ }
};

const CHIPS = [
  { key: "all", label: "All" },
  { key: "blocked", label: "Blocked" },
  { key: "allowed", label: "Allowed" },
  { key: "staff", label: "Staff & admins" },
];

function SummaryCard({ title, value, help, color, selected, onClick, children }) {
  return (
    <Card variant="outlined" sx={{ height: "100%", borderColor: selected ? color : undefined, borderWidth: selected ? 2 : 1 }}>
      <Tooltip title={help}>
        <CardActionArea onClick={onClick} sx={{ height: children ? "auto" : "100%" }}>
          <CardContent>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>{title}</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: value ? color : undefined }}>{value}</Typography>
          </CardContent>
        </CardActionArea>
      </Tooltip>
      {children && <CardContent sx={{ pt: 0 }}>{children}</CardContent>}
    </Card>
  );
}

function StatusChip({ row }) {
  const s = doorStatus(row);
  return (
    <Tooltip title={s.help}>
      <Chip size="small" label={s.label} sx={chipSx(s.color)} />
    </Tooltip>
  );
}

function ReasonText({ row, ctx }) {
  const note = pendingNote(row, ctx);
  return (
    <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
      <Typography variant="body2">{plainReason(row)}</Typography>
      {note && <Typography variant="caption" sx={{ color: "#ff9800" }}>{note}</Typography>}
    </Box>
  );
}

function ActionButton({ row, busy, onAction }) {
  const a = actionFor(row);
  if (!a) return null;
  return (
    <Tooltip title={a.help}>
      <span>
        <Button size="small" variant={a.key === "normal" ? "outlined" : "contained"}
          color={a.key === "block" ? "error" : a.key === "let_in" ? "success" : "inherit"}
          disabled={busy} onClick={() => onAction(row, a)}>
          {a.label}
        </Button>
      </span>
    </Tooltip>
  );
}

// accessFilter: optional deep-link filter for the Advanced list; when set, Advanced opens on load
// (without changing the remembered open/closed preference).
export default function ZkGymDoor({ devices, accessFilter }) {
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.down("sm"));
  const [sn, setSn] = useState("");
  const [data, setData] = useState(null);
  const [refused, setRefused] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null);
  const [advanced, setAdvanced] = useState(() => Boolean(accessFilter) || readAdvanced());
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!sn && devices.length) setSn(devices[0].serial_number);
  }, [devices, sn]);

  const load = useCallback(async () => {
    if (!sn) return;
    setLoading(true);
    try {
      const overview = await zkApi("get", "/hpc/admin/zk/access", { params: { serial_number: sn } });
      const today = overview.data.today;
      const ref = await zkApi("get", "/hpc/admin/zk/refused", { params: { serial_number: sn, from: today, to: today } });
      setData(overview.data);
      setRefused(ref.data?.items || []);
      setError("");
    } catch (e) {
      setError(apiError(e, "Could not load the gym door"));
    } finally {
      setLoading(false);
    }
  }, [sn]);

  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); setRefreshKey((k) => k + 1); };

  const onAction = async (row, a) => {
    if (a.key === "normal") {
      setBusy(true);
      if (await backToNormal(row)) changed();
      setBusy(false);
      return;
    }
    setDialog({ ...row, action: a.action });
  };

  const approve = async () => {
    setBusy(true);
    try {
      await zkApi("post", "/hpc/admin/zk/access/approve-guard",
        { data: { serial_number: sn, count: data.guard.blocks_needed } });
      toast.success(`Approved - ${data.guard.blocks_needed} members will be blocked at the door`);
      setFilter("all");
      changed();
    } catch (e) {
      toast.error(apiError(e, "Could not approve"));
    } finally {
      setBusy(false);
    }
  };

  const toggleAdvanced = (e, open) => { setAdvanced(open); writeAdvanced(open); };
  const openAdvanced = () => toggleAdvanced(null, true);

  const items = useMemo(() => data?.items || [], [data]);
  const settings = data?.settings;
  const paused = !!data?.device?.paused;
  const guard = data?.guard;
  const ctx = { mode: settings?.mode, paused, guardTripped: !!guard?.tripped };
  const summary = useMemo(() => summarize(items, settings, guard), [items, settings, guard]);
  const visible = useMemo(() => {
    const test = SIMPLE_FILTERS[filter] || SIMPLE_FILTERS.all;
    return items.filter((r) => isVisible(r) && test(r, settings) && matchesSearch(r, search));
  }, [items, settings, filter, search]);
  const status = statusLine(settings, paused);

  const columns = [
    {
      field: "full_name", headerName: "Name", flex: 1, minWidth: 200,
      renderCell: ({ row }) => (
        <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{displayName(row)}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>{displayId(row)}</Typography>
        </Box>
      ),
    },
    {
      field: "door", headerName: "Door", width: 120, sortable: false,
      renderCell: ({ row }) => <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}><StatusChip row={row} /></Box>,
    },
    {
      field: "reason", headerName: "Why", flex: 1.4, minWidth: 240, sortable: false,
      renderCell: ({ row }) => <ReasonText row={row} ctx={ctx} />,
    },
    {
      field: "action", headerName: "", width: 150, sortable: false, filterable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <ActionButton row={row} busy={busy} onAction={onAction} />
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Gym door access</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {devices.length > 1 && (
          <TextField select size="small" label="Door" value={sn} onChange={(e) => setSn(e.target.value)} sx={{ minWidth: 200 }}>
            {devices.map((d) => <MenuItem key={d.serial_number} value={d.serial_number}>{d.name || d.serial_number}</MenuItem>)}
          </TextField>
        )}
        <Button size="small" variant="outlined" sx={backButtonSx} startIcon={<RefreshOutlinedIcon />} disabled={loading} onClick={load}>
          Reload
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {settings && <Alert severity={status.severity} sx={{ mb: 2 }}><b>{status.text}</b></Alert>}
      {!data && loading && <CircularProgress size={24} />}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <SummaryCard title={summary.blockedLabel} value={summary.blocked} color="#f44336"
                selected={filter === "blocked"} onClick={() => setFilter("blocked")}
                help={settings.mode === "dryrun" ? "People the door would block if door control were ON"
                  : "People the door has confirmed as blocked"} />
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard title="Need your OK" value={summary.needOk} color="#ff9800" selected={filter === "review"}
                onClick={() => summary.needOk && setFilter("review")}
                help="Many people would be blocked at once - nothing is blocked until you approve">
                {summary.needOk > 0 ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>{summary.needOk} members will be blocked.</Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button size="small" variant="outlined" onClick={() => setFilter("review")}>Review</Button>
                      <Button size="small" variant="contained" color="warning" disabled={busy} onClick={approve}>Approve</Button>
                    </Box>
                  </>
                ) : <Typography variant="caption" sx={{ opacity: 0.7 }}>Nothing to approve</Typography>}
              </SummaryCard>
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard title="Allowed" value={summary.allowed} color="#4caf50" selected={filter === "allowed"}
                onClick={() => setFilter("allowed")} help="People who can open the door" />
            </Grid>
            <Grid item xs={6} md={3}>
              <SummaryCard title="Refused today" value={refused.length} color="#ff9800"
                onClick={() => document.getElementById("gym-door-refused")?.scrollIntoView({ behavior: "smooth" })}
                help="Scans the door refused today" />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            <TextField size="small" placeholder="Search name, membership ID or PIN" value={search}
              onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: { xs: "100%", sm: 300 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            {CHIPS.map((c) => (
              <Chip key={c.key} label={c.label} clickable color={filter === c.key ? "primary" : "default"}
                variant={filter === c.key ? "filled" : "outlined"} onClick={() => setFilter(c.key)} />
            ))}
            {filter === "review" && (
              <Chip label="Waiting for your OK" color="warning" onDelete={() => setFilter("all")} />
            )}
          </Box>

          {small ? (
            <Box>
              {visible.map((row) => (
                <Paper key={row.pin} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{displayName(row)}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>{displayId(row)}</Typography>
                    </Box>
                    <StatusChip row={row} />
                  </Box>
                  <ReasonText row={row} ctx={ctx} />
                  <ActionButton row={row} busy={busy} onAction={onAction} />
                </Paper>
              ))}
              {!visible.length && <Typography variant="body2" sx={{ opacity: 0.7 }}>Nobody here</Typography>}
            </Box>
          ) : (
            <Box sx={{ height: "55vh" }}>
              <DataGrid rows={visible} columns={columns} loading={loading} getRowId={(r) => r.pin}
                getRowHeight={() => "auto"} disableRowSelectionOnClick disableColumnMenu
                localeText={{ noRowsLabel: "Nobody here" }} />
            </Box>
          )}
          {summary.notHandled > 0 && (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              <Link component="button" variant="body2" onClick={openAdvanced}>
                {summary.notHandled} {summary.notHandled === 1 ? "person is" : "people are"} not handled by door control
              </Link>{" "}(see Advanced)
            </Typography>
          )}

          <Typography id="gym-door-refused" variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
            Today at the door - refused
          </Typography>
          {refused.length ? (
            <Paper variant="outlined">
              {refused.slice(0, REFUSED_SHOWN).map((r, i) => (
                <Box key={`${r.pin}-${r.time}-${i}`} sx={{ display: "flex", gap: 2, px: 2, py: 1, borderTop: i ? "1px solid rgba(127,127,127,0.2)" : "none" }}>
                  <Typography variant="body2" sx={{ minWidth: 48, fontWeight: 600 }}>{timeOf(r.time)}</Typography>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {r.member?.full_name || r.device_name || `PIN ${r.pin}`}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1.4, opacity: 0.8 }}>
                    {r.reason ? plainReason({ reason: r.reason }) : "Refused by the door"}
                  </Typography>
                </Box>
              ))}
              {refused.length > REFUSED_SHOWN && (
                <Typography variant="caption" sx={{ display: "block", px: 2, py: 1, opacity: 0.7 }}>
                  Showing the last {REFUSED_SHOWN} of {refused.length}. Full history in Advanced.
                </Typography>
              )}
            </Paper>
          ) : <Typography variant="body2" sx={{ opacity: 0.7 }}>Nobody was refused today.</Typography>}
        </>
      )}

      <Accordion expanded={advanced} onChange={toggleAdvanced} sx={{ mt: 3 }} TransitionProps={{ unmountOnExit: true }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 700 }}>Advanced</Typography>
          <Typography variant="body2" sx={{ ml: 2, opacity: 0.7 }}>technical details, settings, logs, emergency</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ZkDoorAccess devices={devices} refreshKey={refreshKey} onChanged={load} initialFilter={accessFilter} />
        </AccordionDetails>
      </Accordion>

      <DoorAccessDialog target={dialog} sn={sn} today={data?.today} onClose={() => setDialog(null)}
        onDone={() => { setDialog(null); changed(); }} />
    </Box>
  );
}
